import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {VIDEO_EXTENSIONS} from "@/store/constants";
import {create} from "@/store/createStore";
import type {
  TContent,
  TInstagramContent,
  TMediaGalleryItem,
  TMediaProductType,
  TMediaType,
  TNonFunctionKeys,
  TPaginated,
  TPhotoExtended,
} from "@/store/types";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {debounce, isNull, type DebouncedFuncLeading} from "lodash";
import omitBy from "lodash/omitBy";
import qs from "qs";
import {getImageSize} from "react-image-size";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export const getFileExtension = (url: string) => {
  if (!url) {
    return null;
  }

  const regex = /\.([0-9a-z]+)(?:[\?#]|$)/i;
  const match = url.match(regex);

  return match ? match[1] : null;
};

type TFilters = {
  date: string[];
  first_name: string;
  gender: string[];
  liked: boolean | null;
  media_product_type: TMediaProductType | null;
  purchased: boolean | null;
  search?: string | null;
};

type TState = {
  isLoading: boolean;
  calculatedImages: TPhotoExtended[];
  media: TInstagramContent[];
  searchParams: string | null;
  setImages(images: TMediaGalleryItem[], isFirstPage?: boolean): Promise<void>;
  setSearchParams(searchParams: string): void;
  setFilters<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V): void;
  clearCalculatedImages(): void;
  buildSearchParams(): string;
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  reset(): void;
};

const initialState = {
  isLoading: false,
  calculatedImages: [],
  media: [],
  searchParams: null,
} satisfies Pick<TState, TNonFunctionKeys<TState>>;

const initialFilters = {
  gender: [],
  date: [],
  first_name: "",
  search: "",
  media_product_type: null,
  liked: null,
  purchased: null,
};

const createGalleryStore = (filters: TFilters = initialFilters, storePrefix: string = "") =>
  create<TState & {filters: TFilters}>()(
    immer(
      devtools(
        (set, get) => ({
          ...initialState,
          filters,
          debouncedSearchParams: debounce(() => {
            get().buildSearchParams();
          }, 1000),
          buildSearchParams: () => {
            const {filters} = get();
            return qs.stringify(omitBy(filters!, isNull), {
              arrayFormat: "comma",
            });
          },
          clearCalculatedImages: () => {
            set(
              state => {
                state.calculatedImages = [];
                state.media = [];
              },
              false,
              `useCampaignGallery${storePrefix}Store/clearCalculatedImages`,
            );
            queryClient.refetchQueries({
              queryKey: CAMPAIGN_KEYS.qCampaignGallery(
                useCampaignManagementStore.getState().campaign.id!,
                get().searchParams,
              ),
            });
          },
          setFilters: (key, value) => {
            get().debouncedSearchParams.cancel();

            set(
              state => {
                state.filters[key] = value;
              },
              false,
              `useCampaignGallery${storePrefix}Store/setFilters`,
            );
            let searchParams = get().buildSearchParams();
            let timeout = 1000;
            const cachedQuery = queryClient.getQueryData<InfiniteData<AxiosResponse<TPaginated<TMediaGalleryItem>>>>(
              CAMPAIGN_KEYS.qCampaignGallery(useCampaignManagementStore.getState().campaign.id!, get().searchParams),
            );

            if (!!cachedQuery?.pages) {
              timeout = 0;
            }

            set(
              state => {
                state.debouncedSearchParams = debounce(() => {
                  get().setSearchParams(searchParams);
                }, timeout);
              },
              false,
              `useCampaignGallery${storePrefix}Store/setDebouncedSearchParams`,
            );

            get().debouncedSearchParams();
          },
          setSearchParams: searchParams => {
            set(
              state => {
                state.searchParams = searchParams;
              },
              false,
              `useCampaignGallery${storePrefix}Store/buildSearchParams`,
            );
            get().clearCalculatedImages();
          },
          setImages: async (images, isFirstPage = false) => {
            set(
              state => {
                state.isLoading = true;
              },
              false,
              "useCampaignGalleryStore/setLoading",
            );
            const data: TPhotoExtended[] = [];

            for (let index = 0; index < images.length; index++) {
              const type = images[index].type;
              const element = images[index].result;
              let dimensions = {width: 400, height: 400};
              try {
                dimensions =
                  type === "TIKTOK"
                    ? {
                        width: element.thumbnail?.width!,
                        height: element.thumbnail?.height!,
                      }
                    : await getImageSize(
                        (element.thumbnail_file || element.thumbnail_url || element.media_file || element.media_url)!,
                      );
              } catch (error) {
                console.log(error);
              }

              data.push({
                src: (type === "TIKTOK"
                  ? element.thumbnail?.url || element.thumbnail_file
                  : element.thumbnail_file || element.thumbnail_url || element.media_file || element.media_url)!,
                width: dimensions.width,
                height: dimensions.height,
                file_type: getFileExtension(
                  (type === "TIKTOK"
                    ? element.video?.url || element.media_file
                    : element.media_file || element.media_url)!,
                ),
                type:
                  type === "TIKTOK"
                    ? "TIKTOK"
                    : type === "SOURCE"
                      ? "SOURCE"
                      : type === "UGC"
                        ? "UGC"
                        : element.media_product_type!,
                ...element,
                id: element.id!,
                media_file: element.media_file,
                purchased: element.purchased,
                thumbnail_file: element.thumbnail_file,
              });
            }

            data.forEach(item => {
              if (!item.influencer && item.campaign_application?.user) {
                const user = item.campaign_application.user;

                item.influencer = {
                  picture320: user.influencer_profile?.picture320,
                  user: {
                    id: user.id!,
                    first_name: user.first_name!,
                    last_name: user.last_name!,
                  },
                  uuid: user?.influencer_profile?.uuid!,
                };
              }
            });
            set(
              state => {
                state.calculatedImages = [...data];
                state.media = [
                  ...(!isFirstPage ? state.media : []),
                  ...images.map(image => {
                    if (image.type === "TIKTOK") {
                      return {
                        id: image.result.id!,
                        image: (image.result.thumbnail?.url || image.result.thumbnail_file)!,
                        instagram_media: {
                          campaign_application: image.result.campaign_application,
                          id: image.result.id!,
                          liked: image.result.liked,
                          media_file: (image.result.video?.url ?? image.result.media_file)!,
                          media_product_type: "TIKTOK" as TContent,
                          media_type: "VIDEO" as TMediaType,
                          purchased: !!image.result.purchased,
                          thumbnail_file: (image.result.thumbnail?.url ?? image.result.thumbnail_file)!,
                        },
                        user: {
                          first_name: image.result.influencer.first_name ?? image.result.influencer.user?.first_name,
                          influencer_id: image.result.influencer.uuid,
                          last_name: image.result.influencer.last_name ?? image.result.influencer.user?.last_name,
                          picture: image.result.influencer.picture320,
                          username: image.result.influencer.username ?? image.result.influencer.user?.username,
                        },
                      };
                    }

                    if (image.type === "SOURCE") {
                      return {
                        id: image.result.id!,
                        image: image.result.thumbnail_file!,
                        instagram_media: {
                          campaign_application: image.result.campaign_application,
                          id: image.result.id!,
                          liked: image.result.liked,
                          media_file: image.result.media_file,
                          media_product_type: "SOURCE" as TContent,
                          media_type: VIDEO_EXTENSIONS.includes(getFileExtension(image.result.media_file!)!)
                            ? "VIDEO"
                            : ("IMAGE" as TMediaType),
                          thumbnail_file: image.result.thumbnail_file,
                        },
                        user: {
                          first_name: image.result.influencer.first_name,
                          influencer_id: image.result.influencer.uuid,
                          last_name: image.result.influencer.last_name,
                          picture: image.result.influencer.picture320,
                          username: image.result.influencer.username,
                        },
                      };
                    }

                    return {
                      id: image.result.id!,
                      image: image.result.thumbnail_file!,
                      instagram_media: {
                        campaign_application: image.result.campaign_application,
                        children: image.result.children,
                        id: image.result.id!,
                        liked: image.result.liked,
                        media_file: image.result.media_file,
                        media_product_type: image.result.media_product_type as TContent,
                        media_type: image.result.media_type ?? "VIDEO",
                        media_url: image.result.media_url,
                        purchased: !!image.result.purchased,
                        thumbnail_file: image.result.thumbnail_file!,
                        thumbnail_url: image.result.thumbnail_url,
                      },
                      user: {
                        first_name:
                          image.result.influencer?.user?.first_name ??
                          image.result.campaign_application?.user?.first_name,
                        influencer_id:
                          image.result.influencer?.uuid ??
                          image.result.campaign_application?.user?.influencer_profile?.uuid,
                        last_name:
                          image.result.influencer?.user?.last_name ??
                          image.result.campaign_application?.user?.last_name,
                        picture:
                          image.result.influencer?.picture320 ??
                          image.result.campaign_application?.user?.influencer_profile?.picture320,
                        username: image.result.influencer?.user?.username,
                      },
                    };
                  }),
                ];
              },
              false,
              `useCampaignGallery${storePrefix}Store/setImages`,
            );

            set(
              state => {
                state.isLoading = false;
              },
              false,
              `useCampaignGallery${storePrefix}Store/setLoading`,
            );
          },
          reset: () =>
            set({...initialState, filters: initialFilters}, false, `useCampaignGallery${storePrefix}Store/reset`),
        }),
        {name: `useCampaignGallery${storePrefix}Store`},
      ),
    ),
  );

const useCampaignGalleryStore = createGalleryStore(initialFilters);
const useCampaignGalleryAdminStore = createGalleryStore(initialFilters, "Admin");

export {createGalleryStore, useCampaignGalleryAdminStore, useCampaignGalleryStore};
